import logger from "@/lib/logger";
import { importChunkWithRetry } from "@/lib/lazyWithRetry";
import { ensureDeferredCatalogLoaded } from "@/main/messages/deferredCatalog";
import type { NavigationSkeletonHints } from "@/main/navigation/NavigationTypes";
import {
    clearPendingNavigationDestination,
    setPendingNavigationDestination,
} from "@/main/navigation/navigationPendingDestination";

/**
 * FI-3157 (r158): the eager facade over `PageNavigationTransition.ts` (the navigation skeletons' markup builders and
 * the FI-3330 held page, ~58 KB devo), which moved off the startup graph. Design: header-startup-cost design §8.
 *
 * <p>LOADED (the idle warm-up below, or an earlier navigation), every call is synchronous and forwards as before. COLD (the chunk not in yet), `show` records one
 * pending skeleton and draws it when the chunk arrives, unless the navigation settled first: `clear()` (a new
 * navigation), `clear(sameSequence)`, `discard` (a page landed) or `restore` (it failed) cancel it, and a newer `show`
 * replaces it, so a late chunk never covers a page that already landed. Cold, `restore` answers `false` and the rest do
 * nothing: nothing was drawn or held.
 *
 * <p>Retry-only, never a reload (rule 26, FI-2756): the skeleton is an extra over a navigation the member already asked
 * for, the navigation never waits on it, and reloading the page mid-navigation would throw that navigation away. A
 * failed load drops the pending skeleton; the next call retries.
 */
type PageNavigationTransitionModule = typeof import("@/main/navigation/PageNavigationTransition");

type PendingSkeleton = {
    url: URL;
    navigationSequence: number;
    hints?: NavigationSkeletonHints;
};

const CHUNK_KEY = "page-navigation-transition";
/** The idle warm-up's fallback delay where `requestIdleCallback` is missing (Safari), and its idle deadline. */
const IDLE_PRELOAD_FALLBACK_MS = 1500;
const IDLE_PRELOAD_TIMEOUT_MS = 4000;

let loadedModule: PageNavigationTransitionModule | null = null;
let loadingModule: Promise<PageNavigationTransitionModule> | null = null;
let pendingSkeleton: PendingSkeleton | null = null;
let idlePreloadScheduled = false;

function loadPageNavigationTransition(): Promise<PageNavigationTransitionModule> {
    if (loadedModule) {
        return Promise.resolve(loadedModule);
    }
    if (!loadingModule) {
        // FI-5008: the skeletons spell `home.*` keys that live in the DEFERRED catalog half (no eager file spells them),
        // so the module counts as loaded only once that half has landed too; it never rejects, so a degraded catalog
        // still draws while a chunk failure still rejects.
        loadingModule = importChunkWithRetry(
            () => import("@/main/navigation/PageNavigationTransition").then((mod) => ensureDeferredCatalogLoaded().then(() => ({ default: mod }))),
            CHUNK_KEY,
        )
            .then((mod) => {
                // The helper resolves the MODULE ({ default }), not its default export.
                loadedModule = mod.default;
                return mod.default;
            })
            .catch((error: unknown) => {
                loadingModule = null;
                throw error;
            });
    }
    return loadingModule;
}

/** Starts the chunk load, shares its promise, changes nothing on screen. Never rejects. */
export function preloadPageNavigationTransition(): Promise<void> {
    return loadPageNavigationTransition().then(
        () => undefined,
        (error: unknown) => {
            logger.debug("[EnhancedNavigation] Navigation skeleton chunk preload failed.", error);
        },
    );
}

/** The idle rung: one warm-up per document, after startup work, so the first navigation finds the chunk loaded. */
export function schedulePageNavigationTransitionIdlePreload(): void {
    if (idlePreloadScheduled || loadedModule || typeof window === "undefined") {
        return;
    }
    idlePreloadScheduled = true;
    const run = () => {
        void preloadPageNavigationTransition();
    };
    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: IDLE_PRELOAD_TIMEOUT_MS });
    } else {
        window.setTimeout(run, IDLE_PRELOAD_FALLBACK_MS);
    }
}

function cancelPendingSkeleton(navigationSequence?: number): void {
    if (pendingSkeleton && (navigationSequence == null || pendingSkeleton.navigationSequence === navigationSequence)) {
        pendingSkeleton = null;
    }
}

export function showNavigationTransitionSkeleton(
    url: URL,
    navigationSequence: number,
    hints?: NavigationSkeletonHints,
): void {
    // FI-4834: the bottom bar marks the destination tab current while the skeleton is up, cold or loaded.
    setPendingNavigationDestination(url, navigationSequence);
    if (loadedModule) {
        pendingSkeleton = null;
        loadedModule.showNavigationTransitionSkeleton(url, navigationSequence, hints);
        return;
    }

    const pending: PendingSkeleton = { url, navigationSequence, hints };
    pendingSkeleton = pending;
    loadPageNavigationTransition().then(
        (mod) => {
            // Identity, not sequence: a newer show replaced it, or something that settled the navigation cancelled it.
            if (pendingSkeleton !== pending) {
                return;
            }
            pendingSkeleton = null;
            mod.showNavigationTransitionSkeleton(pending.url, pending.navigationSequence, pending.hints);
        },
        (error: unknown) => {
            if (pendingSkeleton === pending) {
                pendingSkeleton = null;
            }
            logger.debug("[EnhancedNavigation] Navigation skeleton chunk failed; navigating without a skeleton.", error);
        },
    );
}

export function clearNavigationTransitionSkeleton(navigationSequence?: number): void {
    cancelPendingSkeleton(navigationSequence);
    clearPendingNavigationDestination(navigationSequence);
    loadedModule?.clearNavigationTransitionSkeleton(navigationSequence);
}

/** FI-3330: puts the held page back. False when nothing was held, which is always the case before the chunk loaded. */
export function restoreHeldNavigationPage(): boolean {
    cancelPendingSkeleton();
    return loadedModule ? loadedModule.restoreHeldNavigationPage() : false;
}

/** FI-3330: a page landed, so the held page (if any) is unmounted for good, and a skeleton still pending is dropped. */
export function discardHeldNavigationPage(): void {
    cancelPendingSkeleton();
    loadedModule?.discardHeldNavigationPage();
}

/** Test seam: forgets the pending skeleton and the idle flag; with `unload`, the loaded module too (a cold start). */
export function resetPageNavigationTransitionLazyForTests(options: { unload?: boolean } = {}): void {
    pendingSkeleton = null;
    idlePreloadScheduled = false;
    clearPendingNavigationDestination();
    if (options.unload) {
        loadedModule = null;
        loadingModule = null;
    }
}
